
import { Component, Vue, Prop, Watch, Ref, Emit } from 'vue-property-decorator'
import EmblaCarousel, { EmblaCarouselType, EmblaOptionsType } from 'embla-carousel'
import ClassNames from 'embla-carousel-class-names'
import Fade from 'embla-carousel-fade'
import Autoplay from 'embla-carousel-autoplay'
import AutoScroll from 'embla-carousel-auto-scroll'

@Component({
  components: {}
})
export default class extends Vue {
  @Prop({ default: () => [] }) list: unknown[]
  @Prop({ default: true }) isList: unknown[]
  @Prop({ default: true }) showDots: boolean
  @Prop({ default: true }) autoPlay: boolean
  @Prop({ default: true }) loop: boolean
  @Prop({ default: false }) scroll: boolean
  @Prop({ default: false }) scrollBack: boolean
  @Prop({ default: false }) fade: boolean
  @Prop({ default: false }) axisY: boolean
  @Prop({ default: false }) noTouchMove: boolean
  @Prop({ default: true }) scrollStopOnMouseEnter: boolean
  @Prop({ default: 1 }) scrollSpeed: number
  @Prop({ default: 25 }) duration: number
  @Prop({ default: 3000 }) autoPlayDuration: number
  @Prop({ default: false }) useClassName: boolean
  @Prop({ default: false }) loadMore: boolean
  @Prop({ default: false }) dragFree: boolean

  @Ref()
  emblaNode: HTMLDivElement

  emblaApi: EmblaCarouselType

  options: EmblaOptionsType

  activeIndex: number = 0

  loadingMore: boolean = false
  hasMoreToLoad: boolean = true
  listenForScroll: boolean = true

  // 切换回调事件
  @Emit('change')
  change() {
    this.activeIndex = this.emblaApi.selectedScrollSnap()

    return this.activeIndex
  }

  startScroll() {
    const autoScroll = this.emblaApi.plugins()?.autoScroll

    if (!autoScroll) return

    this.scroll && autoScroll.play()
  }

  stopScroll() {
    const autoScroll = this.emblaApi.plugins()?.autoScroll

    if (!autoScroll) return

    autoScroll.stop()
  }

  // 指示点跳转操作
  dotScrollTo(index) {
    this.emblaApi.scrollTo(index)
  }

  hanldeScrollTo(index) {
    const autoScroll = this.emblaApi.plugins()?.autoScroll

    if (!autoScroll) return

    if (autoScroll.isPlaying()) {
      autoScroll.stop()
    }

    this.emblaApi.scrollTo(index)

    this.scroll && autoScroll.play()
  }

  // 上一页
  handlePrev() {
    this.emblaApi.scrollPrev()
  }

  // 下一页
  handleNext() {
    this.emblaApi.scrollNext()
  }

  handleAutoPlay() {
    const autoPlay = this.emblaApi.plugins().autoplay

    if (!autoPlay) return

    autoPlay.play()
  }

  // 拓展无限滚动功能
  onEndLoadMore(hasMore: boolean) {
    const lastSlide = this.emblaApi.slideNodes().length - 1
    const isLastSlideInView = this.emblaApi.slidesInView().includes(lastSlide)
    const loadMore = !this.loadingMore && isLastSlideInView

    if (!hasMore) {
      this.hasMoreToLoad = false
      this.emblaApi.off('scroll', this.onScroll)
    }

    this.loadingMore = loadMore || isLastSlideInView
  }
  onScroll() {
    if (!this.listenForScroll) return

    const lastSlide = this.emblaApi.slideNodes().length - 1
    const isLastSlideInView = this.emblaApi.slidesInView().includes(lastSlide)
    const loadMore = !this.loadingMore && isLastSlideInView

    if (loadMore) {
      this.listenForScroll = false

      setTimeout(() => {
        this.$emit('loadMore')
      }, 300)
    }

    this.loadingMore = loadMore || isLastSlideInView
  }

  @Watch('list')
  updateAddScrollListener(val) {
    //列表第一次是空，监听有值时的情况
    // console.log('addScrollListener')

    if (val.length > 0 && this.loadMore) {
      this.emblaApi.on('scroll', this.onScroll)
    }
  }
  reloadEmbla() {
    const oldEngine = this.emblaApi.internalEngine()

    this.emblaApi.reInit()
    const newEngine = this.emblaApi.internalEngine()
    const copyEngineModules = ['scrollBody', 'location', 'offsetLocation', 'previousLocation', 'target']
    copyEngineModules.forEach(engineModule => {
      Object.assign(newEngine[engineModule], oldEngine[engineModule])
    })

    newEngine.translate.to(oldEngine.location.get())
    const { index } = newEngine.scrollTarget.byDistance(0, false)
    newEngine.index.set(index)
    newEngine.animation.start()

    this.loadingMore = false
    this.listenForScroll = true
  }
  reloadAfterPointerUp() {
    this.emblaApi.off('pointerUp', this.reloadAfterPointerUp)
    this.reloadEmbla()
  }

  // 初始化swiper配置
  init(): void {
    if (!this.emblaNode) return

    this.options = {
      duration: this.duration,
      loop: this.loop,
      axis: this.axisY ? 'y' : 'x',
      watchDrag: !this.noTouchMove ? true : false,
      dragFree: this.dragFree
    }

    if (this.loadMore) {
      this.options = {
        ...this.options,
        dragFree: true,
        watchSlides: emblaApi => {
          const engine = emblaApi.internalEngine()
          if (this.hasMoreToLoad && engine.dragHandler.pointerDown()) {
            const boundsActive = engine.limit.reachedMax(engine.target.get())
            engine.scrollBounds.toggleActive(boundsActive)
            emblaApi.on('pointerUp', this.reloadAfterPointerUp)
          } else {
            this.reloadEmbla()
          }
        }
      }
    }

    let plugins: any = [
      Autoplay({ playOnInit: this.autoPlay, delay: this.autoPlayDuration, stopOnInteraction: !this.autoPlay }),
      AutoScroll({
        playOnInit: this.scroll,
        stopOnInteraction: !this.scroll,
        stopOnMouseEnter: this.scrollStopOnMouseEnter,
        direction: this.scrollBack ? 'backward' : 'forward',
        speed: this.scrollSpeed
      })
    ]

    if (this.fade) {
      plugins = [...plugins, Fade()]
    }

    if (this.useClassName) {
      plugins = [...plugins, ClassNames()]
    }

    this.emblaApi = EmblaCarousel(this.emblaNode, this.options, plugins)

    this.emblaApi.on('init', this.change)
    this.emblaApi.on('select', this.change)
    this.emblaApi.on('reInit', () => {
      // 组件刷新时要同步activeIndex值
      if (!this.loadMore) {
        this.activeIndex = 0
      }
    })

    //  无法直接调用play，此时autoplay插件还未初始化成功
    // this.autoPlayer.play()
  }

  created(): void {}

  mounted(): void {
    console.log('swiper注册成功！')
    this.init()
  }

  updated(): void {}

  destroyed(): void {}
}
